import { useEffect, useState } from 'react';
import moment from 'moment';
import { useForm } from 'react-hook-form';
import { useLocation, useSearchParams } from 'react-router';
import { intlShape } from '../components/base/IntlProviderWrapper';
import notificationDecorator from '../modules/Notifications/notificationDecorator';
import {
  DEFAULT_ITEMS_OF_PAGE,
  EVACUATIONS_STATUS_NAMES,
  GEOGRAPHY_TYPE_COLOR, GEOGRAPHY_Z_INDEX,
  STATUS_COLOR_CODE,
  ZONE_TYPE,
} from '../configs';
import { required } from './validation';
import { compactUrlValues } from './filterUrl';
import { normalizeApiError } from './apiError';

export const getResponseError = (response, defaultMessage = '') => {
  const { formatMessage } = intlShape;
  const normalized = normalizeApiError(response, {
    defaultMessage: defaultMessage || formatMessage({ id: 'error.default' }),
  });
  return normalized.message;
};

export const setFormError = (response, setError = () => {}) => {
  if (!response) {
    return;
  }
  const { fieldErrors } = normalizeApiError(response);
  Object.entries(fieldErrors).forEach(([field, message], index) => {
    setError(field, { type: 'server', message }, { shouldFocus: !index });
  });
};

export const getFirstFormError = (errors = {}, prevName = []) => {
  const keys = Object.keys(errors);
  if (!keys || !keys.length) return null;
  const firstElement = errors[keys[0]];
  if (firstElement && firstElement.type && firstElement.message && firstElement.ref) {
    firstElement.name = [...prevName, keys[0]].join('.');
    return firstElement;
  }
  return getFirstFormError(firstElement, [...prevName, keys[0]]);
};

export const isObject = object => object != null && typeof object === 'object';

export const normalizeArrayValue = value => {
  if (Array.isArray(value)) {
    return value;
  }
  if (value === null || typeof value === 'undefined' || value === '') {
    return [];
  }
  if (typeof value === 'string') {
    return value.split(',').map(item => item.trim()).filter(Boolean);
  }
  return [value];
};

export const isSameScalarValue = (first, second) => (
  first === second || String(first) === String(second)
);

export const deepEqual = (object1, object2) => {
  const keys1 = Object.keys(object1);
  const keys2 = Object.keys(object2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    const val1 = object1[key];
    const val2 = object2[key];
    const areObjects = isObject(val1) && isObject(val2);
    if (
      (areObjects && !deepEqual(val1, val2))
      || (!areObjects && val1 !== val2)
    ) {
      return false;
    }
  }

  return true;
};

export const isClear = obj => Object.values(obj).reduce((acc, cur) => {
  const value = cur;
  if (isObject(value)) {
    return acc && isClear(value);
  } if (Array.isArray(value)) {
    return acc && !value.length;
  }
  return acc && !value;
}, true);

export const fallbackCopyTextToClipboard = text => {
  if (typeof document === 'undefined' || !document.body) {
    return false;
  }
  const textArea = document.createElement('textarea');
  textArea.value = text;

  textArea.style.top = '0';
  textArea.style.left = '0';
  textArea.style.position = 'fixed';

  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  try {
    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    return successful;
  } catch (err) {
    document.body.removeChild(textArea);
    return false;
  }
};

export const copyTextToClipboard = text => {
  if (!navigator.clipboard || !window.isSecureContext) {
    return Promise.resolve(fallbackCopyTextToClipboard(text));
  }
  return navigator.clipboard.writeText(text)
    .then(() => true)
    .catch(() => fallbackCopyTextToClipboard(text));
};

export const getPathNameWithOutParameters = (path = '') => {
  if (!path) {
    return path;
  }
  return path.split(':')[0];
};

export const addZero = (len = 1, part = '') => {
  const arrayLen = len - String(part).length >= 0 ? len - String(part).length : 0;
  return (
    Array(arrayLen)
      .fill('0')
      .join('') + String(part)
  );
};

export const responseLog = response => window.console.error(response);

export const responseLogWithNotification = (response, title = '', message = '') => {
  const {
    formatMessage,
  } = intlShape;
  window.console.error(response);
  notificationDecorator({
    title: title || formatMessage({ id: 'load.data' }),
    message: getResponseError(response, message) || formatMessage({ id: 'failed' }),
    isError: true,
  });
};

export const getPaginationOpts = (listMeta = {}, opts = {}) => {
  const page = opts && !Number.isNaN(Number(opts.page))
    ? Number(opts.page)
    : listMeta && listMeta.page && !!Number(listMeta.page)
      ? Number(listMeta.page)
      : 1;
  const limit = opts && opts.limit && !!Number(opts.limit)
    ? Number(opts.limit)
    : listMeta && listMeta.limit && !!Number(listMeta.limit)
      ? Number(listMeta.limit)
      : DEFAULT_ITEMS_OF_PAGE;
  return {
    page,
    limit,
  };
};

export const getJSONFromString = value => {
  if (!value) {
    return null;
  }
  try {
    return JSON.parse(value);
  } catch (e) {
    return null;
  }
};

export class PermissionDeniedError extends Error {
  constructor(message, options) {
    // Need to pass `options` as the second parameter to install the "cause" property.
    super(message, options);
    this.status = 403;
  }
}

export function getJsonFromStorage(name, defaultValue = null) {
  const value = localStorage.getItem(name);
  if (value) {
    try {
      return JSON.parse(value);
    } catch (e) {
      return defaultValue;
    }
  }
  return defaultValue;
}

export const useStoreState = function useStoreState(initialState, name) {
  const [value, setter] = useState(getJsonFromStorage(name) || initialState);
  const setValue = value => {
    setter(value);
  };
  const save = value => {
    localStorage.setItem(name, JSON.stringify(value));
  };
  const reset = () => setValue(getJsonFromStorage(name) || initialState);
  return [value, setValue, reset, save];
};

export const useFilterForm = function useFilterForm(defaultFilterValues = {}, updateStateCallback, excludeParams = []) {
  const [searchParams, setSearchParams] = useSearchParams();

  const setUrlData = data => {
    setSearchParams(data);
  };

  const getUrlData = () => {
    const res = {};
    const p = new URLSearchParams(window.location.search);
    for (const entry of p.entries()) {
      const [key, value] = entry;
      res[key] = value;
    }
    return res;
  };

  const location = useLocation();
  const useFormObject = useForm({
    defaultValues: { ...defaultFilterValues, ...getUrlData() },
    resetOptions: {
      keepValues: false,
      keepDirtyValues: false,
      keepDefaultValues: false,
    },
  });
  const {
    watch, getValues, setValue,
  } = useFormObject;

  const getClearValues = () => {
    const v = getValues();
    return Object.keys(v).reduce((acc, key) => {
      if (excludeParams?.length && excludeParams.includes(key)) {
        return acc;
      }
      acc[key] = v[key];
      return acc;
    }, {});
  };

  useEffect(() => {
    const fv = getValues();
    const uv = getUrlData();
    const res = Object.keys(fv).reduce((acc, key) => {
      acc[key] = uv[key] || '';
      return acc;
    }, {});

    for (const key of Object.keys(res)) {
      setValue(key, res[key]);
    }
  }, [location.search]);
  useEffect(() => {
    const subscription = watch(values => {
      updateStateCallback({ page: 1 });
      const newValues = compactUrlValues(values);
      setUrlData(newValues);
    });
    return () => subscription.unsubscribe();
  }, [watch]);
  return { ...useFormObject, getClearValues };
};

window.updateTimers = [];

export const useUpdatePeriod = function useUpdatePeriod(callback = Promise, updatePeriod, noPromise = false) {
  let timer = null;
  let period = updatePeriod || 0;
  let stopped = false;
  const setPeriod = value => {
    period = value || 0;
    load();
    return destroy;
  };
  const destroy = () => {
    stopped = true;
    clear();
  };

  const clear = () => {
    if (timer) {
      const index = window.updateTimers.findIndex(i => timer === i);
      if (index > -1) {
        window.updateTimers.splice(index, 1);
      }
      clearTimeout(timer);
    }
  };
  const load = () => {
    clear();
    const c = callback();
    if (!noPromise) {
      // пока идет запрос нет таймера это пораждает баг с переключением на другой экран.
      c.finally(values => {
        if (period && !stopped) {
          createTimer();
        }
      });
    } else if (period) {
      createTimer();
    }
  };

  const createTimer = () => {
    timer = setTimeout(() => load(), period * 1000);
    window.updateTimers.push(timer);
  };

  return [setPeriod, destroy, clear, load, createTimer];
};

export const nameBySave = (text = '') => {
  const res = text.replace(/[^a-zа-яё0-9 ._№\-\s]/gi, '_');
  return res.replace(/^[^a-zа-яё0-9\s]*/gi, '');
};

export const getMomentValue = (value, format) => {
  let v = moment(value, format);
  if (!v || !v.isValid()) {
    v = moment();
  }
  return v;
};

export const sizeFormatter = (size = 0) => {
  const { formatNumber } = intlShape;
  const k = 1024;
  const sizes = ['byte', 'kilobyte', 'megabyte', 'gigabyte', 'terabyte'];
  if (!size) {
    return formatNumber(0, { style: 'unit', unit: 'byte' });
  }
  const i = Math.floor(Math.log(size) / Math.log(k));
  const shortSize = parseFloat((size / k ** i).toFixed(2));
  return formatNumber(shortSize, { style: 'unit', unit: sizes[i] });
};

export const getIsRequired = (dtoObject, customValidator) => (dtoObject
      && dtoObject.validators
      && dtoObject.validators.length
      && !!dtoObject.validators.find(validator => validator.toString() === required.toString()))
    || !!customValidator.find(validator => validator.toString() === required.toString());

export const createClearObject = fields => Object.keys(fields).reduce((acc, key) => {
  const field = fields[key];
  const isArray = Array.isArray(field);
  if (field && typeof field === 'object' && !isArray) {
    acc[key] = createClearObject(field);
  } else if (isArray) {
    acc[key] = [];
  } else {
    acc[key] = '';
  }
  return acc;
}, {});

export const detectExt = base => new Promise((resolve, reject) => {
  const i = new XMLHttpRequest();
  i.onload = resolve;
  i.onerror = reject;
  i.open('GET', `chrome-extension://${base}/views/app.html`);
  i.send();
});

export const urlController = (searchParams, setSearchParams) => {
  const handleSetUrlParams = params => {
    const result = Object.keys(params).reduce((acc, c) => {
      if (params[c]) {
        acc[c] = params[c];
      }
      return acc;
    }, {});
    setSearchParams(result);
  };
  const handleAddUrlParams = params => {
    const res = {};
    for (const entry of searchParams.entries()) {
      const [key, value] = entry;
      res[key] = value;
    }
    const keys = Object.keys(params);
    for (const key of keys) {
      res[key] = params[key];
    }
    setSearchParams(res);
  };
  const handleRemoveUrlParams = (names = []) => {
    const res = {};
    for (const entry of searchParams.entries()) {
      const [key, value] = entry;
      if (!names.includes(key)) {
        res[key] = value;
      }
    }
    setSearchParams(res);
  };
  return {
    handleSetUrlParams,
    handleAddUrlParams,
    handleRemoveUrlParams,
  };
};

export const getColorByType = type => GEOGRAPHY_TYPE_COLOR[type] || '#1665A750';
export const getZIndexByType = type => GEOGRAPHY_Z_INDEX[type] || 1;

export const objToForm = obj => {
  const form_data = new FormData();
  for (const key of Object.keys(obj)) {
    form_data.append(key, obj[key]);
  }
  return form_data;
};

export const createGeoObject = coordinates => ({
  type: 'Feature',
  properties: {
    timestamp: new Date().getTime(),
    stop_id: 'a2c9a65f-fd85-463e-9564-fc95ea473f7d',
    name: 'Geo object',
  },
  geometry: {
    type: 'Point',
    coordinates,
  },
});

export const getPointData = item => ({
  balloonContentHeader: '<div>Остановка</div>',
  balloonContentBody: `Имя ${item?.name || ''} Координаты ${item?.coordinates}`,
  balloonContentFooter: 'текст футера',
  clusterCaption: 'Остановка ',
});

export const getPointOptions = (item, draggable = false) => {
  const { status } = item;
  const colorName = EVACUATIONS_STATUS_NAMES[status];
  const color = STATUS_COLOR_CODE[colorName];
  return {
    preset: 'islands#icon',
    iconColor: color || '#1665A7',
    draggable,
  };
};

export const isCoordinatePair = point => (
  Array.isArray(point)
  && point.length >= 2
  && Number.isFinite(Number(point[0]))
  && Number.isFinite(Number(point[1]))
);

export const getCoordinatePairs = coordinates => {
  if (!Array.isArray(coordinates)) {
    return [];
  }
  if (isCoordinatePair(coordinates)) {
    return [coordinates.map(Number)];
  }
  return coordinates.reduce((acc, item) => [...acc, ...getCoordinatePairs(item)], []);
};

export const getBoundsFromPoints = points => {
  const coordinatePairs = getCoordinatePairs(points);
  if (!coordinatePairs.length) {
    return null;
  }
  let minLon = Infinity;
  let maxLon = -Infinity;
  let minLat = Infinity;
  let maxLat = -Infinity;

  coordinatePairs.forEach(([lon, lat]) => {
    minLon = Math.min(minLon, lon);
    maxLon = Math.max(maxLon, lon);
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
  });

  return [
    [minLon, minLat],
    [maxLon, maxLat],
  ];
};

export const getBoundsFromPolygons = polygons => {
  if (!Array.isArray(polygons) || !polygons.length) {
    return null;
  }
  let minLon = Infinity;
  let maxLon = -Infinity;
  let minLat = Infinity;
  let maxLat = -Infinity;

  polygons.forEach(polygon => {
    getCoordinatePairs(polygon).forEach(([lon, lat]) => {
      minLon = Math.min(minLon, lon);
      maxLon = Math.max(maxLon, lon);
      minLat = Math.min(minLat, lat);
      maxLat = Math.max(maxLat, lat);
    });
  });

  if (minLon === Infinity) {
    return null;
  }
  return [
    [minLon, minLat],
    [maxLon, maxLat],
  ];
};
